import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCategorias, useConceptos, useProveedores } from '@/hooks/useFlujo';
import { cliente } from '@/lib/cliente';
import type {
  BancoPago,
  Categoria,
  Concepto,
  EliminarConceptosResultado,
  Proveedor,
} from '@adelante/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Archive, ChevronDown, ChevronRight, Edit, Eye, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

const NINGUN = '__ninguno__';

function aplanarCategorias(cats: Categoria[], nivel = 0): Array<Categoria & { nivel: number }> {
  const result: Array<Categoria & { nivel: number }> = [];
  for (const c of cats) {
    result.push({ ...c, nivel });
    if (c.hijos?.length) result.push(...aplanarCategorias(c.hijos, nivel + 1));
  }
  return result;
}

// ─── Dialog crear/editar concepto ────────────────────────────────────────────

function DialogConcepto({
  concepto,
  categoriaInicial,
  categorias,
  proveedores,
  open,
  onClose,
}: {
  concepto?: Concepto;
  /** Categoría ya elegida al crear un concepto desde el "+" de su encabezado. */
  categoriaInicial?: number;
  categorias: Categoria[];
  proveedores: Proveedor[];
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    nombre: concepto?.nombre ?? '',
    moneda: concepto?.moneda ?? 'C',
    tipo: concepto?.tipo ?? 'recurrente',
    categoriaId: concepto?.categoriaId
      ? String(concepto.categoriaId)
      : categoriaInicial
        ? String(categoriaInicial)
        : '',
    proveedorId: concepto?.proveedorId ? String(concepto.proveedorId) : NINGUN,
    notas: concepto?.notas ?? '',
    tieneObligaciones: concepto?.tieneObligaciones ?? false,
    periodicidad: concepto?.periodicidad ?? 'mensual',
    montoCuotaFijo: concepto?.montoCuotaFijo ? String(concepto.montoCuotaFijo) : '',
    // 'NINGUNO' = sin banco por defecto (el Select necesita un valor de texto).
    bancoPorDefecto: (concepto?.bancoPorDefecto ?? 'NINGUNO') as BancoPago | 'NINGUNO',
  });
  const [guardando, setGuardando] = useState(false);
  const catsAplanas = aplanarCategorias(categorias);
  const relacionadaElegida = proveedores.some(
    (p) => p.esEmpresaRelacionada && String(p.id) === form.proveedorId,
  );

  const handleGuardar = async () => {
    if (!form.nombre.trim() || !form.categoriaId) {
      toast.error('Nombre y categoría son requeridos');
      return;
    }
    setGuardando(true);
    try {
      const montoCuotaFijoNum = form.montoCuotaFijo.trim()
        ? Number(form.montoCuotaFijo.replace(/[₡$,\s]/g, ''))
        : null;
      const payload = {
        nombre: form.nombre.trim(),
        moneda: form.moneda as 'C' | 'D',
        tipo: form.tipo as 'recurrente' | 'esporadico',
        categoriaId: Number(form.categoriaId),
        proveedorId:
          form.proveedorId && form.proveedorId !== NINGUN ? Number(form.proveedorId) : null,
        notas: form.notas.trim() || null,
        tieneObligaciones: form.tieneObligaciones,
        periodicidad: form.tieneObligaciones
          ? (form.periodicidad as 'mensual' | 'bimestral' | 'trimestral' | 'semestral')
          : null,
        montoCuotaFijo:
          form.tieneObligaciones && montoCuotaFijoNum && !Number.isNaN(montoCuotaFijoNum)
            ? montoCuotaFijoNum
            : null,
        bancoPorDefecto: form.bancoPorDefecto === 'NINGUNO' ? null : form.bancoPorDefecto,
      };
      if (concepto) {
        const res = await cliente.conceptos.actualizar(concepto.id, payload);
        if (res.error) throw new Error(res.error.mensaje);
        toast.success('Concepto actualizado');
      } else {
        const res = await cliente.conceptos.crear(payload);
        if (res.error) throw new Error(res.error.mensaje);
        toast.success('Concepto creado');
      }
      await qc.invalidateQueries({ queryKey: ['conceptos'] });
      qc.invalidateQueries({ queryKey: ['semana'] });
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{concepto ? 'Editar concepto' : 'Nuevo concepto'}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <div className="space-y-1">
            <Label>Nombre *</Label>
            <Input
              value={form.nombre}
              onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
              placeholder="Nombre del concepto"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>Moneda *</Label>
              <Select
                value={form.moneda}
                onValueChange={(v) => setForm((f) => ({ ...f, moneda: v as 'C' | 'D' }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="C">₡ Colones</SelectItem>
                  <SelectItem value="D">$ Dólares</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Tipo</Label>
              <Select
                value={form.tipo}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, tipo: v as 'recurrente' | 'esporadico' }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recurrente">Recurrente</SelectItem>
                  <SelectItem value="esporadico">Esporádico</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Categoría *</Label>
              <Select
                value={form.categoriaId}
                onValueChange={(v) => setForm((f) => ({ ...f, categoriaId: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="..." />
                </SelectTrigger>
                <SelectContent>
                  {catsAplanas.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {'  '.repeat(c.nivel)}
                      {c.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Proveedor (opcional — para archivo BAC)</Label>
              <Select
                value={relacionadaElegida ? NINGUN : form.proveedorId}
                onValueChange={(v) => setForm((f) => ({ ...f, proveedorId: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sin proveedor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NINGUN}>Sin proveedor</SelectItem>
                  {proveedores
                    .filter((p) => !p.esEmpresaRelacionada)
                    .map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.nombre}
                        {p.cuentas.length > 0 ? ' ✓' : ''}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Empresa relacionada (el pago sale a su cuenta)</Label>
              <Select
                value={relacionadaElegida ? form.proveedorId : NINGUN}
                onValueChange={(v) => setForm((f) => ({ ...f, proveedorId: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Ninguna" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NINGUN}>Ninguna</SelectItem>
                  {proveedores
                    .filter((p) => p.esEmpresaRelacionada)
                    .map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.nombre}
                        {p.cuentas.length > 0 ? ' ✓' : ''}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Banco por defecto */}
          <div
            className={`flex items-center justify-between gap-3 border rounded-md p-3 transition-colors ${form.bancoPorDefecto !== 'NINGUNO' ? 'border-primary/40 bg-primary/5' : 'bg-muted/20'}`}
          >
            <div>
              <Label className="text-sm font-medium">Banco por el que se paga</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Queda asignado en cada semana. Se puede cambiar por semana, o dejarlo sin banco y
                usar "Sugerir bancos".
              </p>
            </div>
            <Select
              value={form.bancoPorDefecto}
              onValueChange={(v) =>
                setForm((f) => ({ ...f, bancoPorDefecto: v as BancoPago | 'NINGUNO' }))
              }
            >
              <SelectTrigger className="w-32 shrink-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NINGUNO">Sin banco</SelectItem>
                <SelectItem value="BAC">BAC</SelectItem>
                <SelectItem value="BN">Banco Nacional</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Obligaciones periódicas */}
          <div className="border rounded-md p-3 space-y-3 bg-muted/20">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Obligaciones periódicas</Label>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, tieneObligaciones: !f.tieneObligaciones }))}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${form.tieneObligaciones ? 'bg-primary' : 'bg-muted-foreground/30'}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.tieneObligaciones ? 'translate-x-4' : 'translate-x-0.5'}`}
                />
              </button>
            </div>
            {form.tieneObligaciones && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Periodicidad</Label>
                  <Select
                    value={form.periodicidad}
                    onValueChange={(v) =>
                      setForm((f) => ({
                        ...f,
                        periodicidad: v as 'mensual' | 'bimestral' | 'trimestral' | 'semestral',
                      }))
                    }
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mensual">Mensual</SelectItem>
                      <SelectItem value="bimestral">Bimestral</SelectItem>
                      <SelectItem value="trimestral">Trimestral</SelectItem>
                      <SelectItem value="semestral">Semestral</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    Monto fijo por cuota (opcional)
                  </Label>
                  <Input
                    value={form.montoCuotaFijo}
                    onChange={(e) => setForm((f) => ({ ...f, montoCuotaFijo: e.target.value }))}
                    placeholder="Variable — dejar vacío"
                    className="h-8 tabular-nums text-sm"
                  />
                </div>
              </div>
            )}
            {!form.tieneObligaciones && (
              <p className="text-xs text-muted-foreground">
                Activá esto para CCSS, IVA, préstamos u otros pagos que se acumulan mes a mes.
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={guardando}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={guardando}>
            {guardando ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Fila de concepto ─────────────────────────────────────────────────────────

function FilaConcepto({
  concepto,
  onEditar,
  seleccionado,
  onSeleccion,
}: {
  concepto: Concepto;
  onEditar: () => void;
  seleccionado: boolean;
  onSeleccion: (marcado: boolean) => void;
}) {
  const qc = useQueryClient();

  const toggleTipo = async () => {
    const nuevoTipo = concepto.tipo === 'recurrente' ? 'esporadico' : 'recurrente';
    try {
      await cliente.conceptos.actualizar(concepto.id, { tipo: nuevoTipo });
      toast.success(`Tipo cambiado a ${nuevoTipo}`);
      qc.invalidateQueries({ queryKey: ['conceptos'] });
      qc.invalidateQueries({ queryKey: ['semana'] });
    } catch {
      toast.error('Error al cambiar tipo');
    }
  };

  const toggleActivo = async () => {
    try {
      await cliente.conceptos.actualizar(concepto.id, { activo: !concepto.activo });
      toast.success(concepto.activo ? 'Concepto archivado' : 'Concepto reactivado');
      qc.invalidateQueries({ queryKey: ['conceptos'] });
      qc.invalidateQueries({ queryKey: ['semana'] });
    } catch {
      toast.error('Error');
    }
  };

  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 border-b last:border-0 ${!concepto.activo ? 'opacity-40' : ''}`}
    >
      <Checkbox
        checked={seleccionado}
        onCheckedChange={(v) => onSeleccion(v === true)}
        aria-label={`Seleccionar ${concepto.nombre}`}
      />
      {/* Moneda */}
      <Badge
        variant={concepto.moneda === 'C' ? 'secondary' : 'outline'}
        className="text-xs w-5 justify-center shrink-0 font-mono"
      >
        {concepto.moneda === 'C' ? '₡' : '$'}
      </Badge>

      {/* Nombre */}
      <span className="text-sm flex-1 truncate">{concepto.nombre}</span>

      {/* Proveedor */}
      {concepto.proveedor && (
        <span className="text-xs text-green-700 shrink-0 hidden md:block">
          {concepto.proveedor.nombre}
          {concepto.proveedor.iban ? ' ✓' : ''}
        </span>
      )}

      {/* Badge del banco por defecto */}
      {concepto.bancoPorDefecto && (
        <Badge
          variant="outline"
          className={`text-xs shrink-0 ${concepto.bancoPorDefecto === 'BN' ? 'border-lime-300 text-lime-800' : 'border-red-300 text-red-700'}`}
        >
          {concepto.bancoPorDefecto}
        </Badge>
      )}

      {/* Badge obligaciones */}
      {concepto.tieneObligaciones && (
        <Badge
          variant="secondary"
          className="text-xs shrink-0 gap-1"
          title={`Periodicidad: ${concepto.periodicidad}${concepto.montoCuotaFijo ? ` · ₡${concepto.montoCuotaFijo.toLocaleString('es-CR')} fijo` : ' · variable'}`}
        >
          📋 {concepto.periodicidad ?? 'mensual'}
        </Badge>
      )}

      {/* Badge tipo — clickeable */}
      <button
        type="button"
        onClick={toggleTipo}
        title={`Tipo: ${concepto.tipo}. Clic para cambiar.`}
        className="shrink-0"
      >
        <Badge
          variant={concepto.tipo === 'esporadico' ? 'warning' : 'success'}
          className="text-xs cursor-pointer hover:opacity-80 transition-opacity"
        >
          {concepto.tipo === 'esporadico' ? 'Esporádico' : 'Recurrente'}
        </Badge>
      </button>

      {/* Editar */}
      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onEditar}>
        <Edit className="h-3.5 w-3.5" />
      </Button>

      {/* Archivar / Reactivar */}
      <Button
        variant="ghost"
        size="icon"
        className={`h-7 w-7 shrink-0 ${concepto.activo ? 'text-muted-foreground hover:text-destructive' : 'text-green-600'}`}
        onClick={toggleActivo}
        title={concepto.activo ? 'Archivar (desactivar)' : 'Reactivar'}
      >
        {concepto.activo ? <Archive className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
}

// ─── Grupo por categoría ──────────────────────────────────────────────────────

function GrupoConceptos({
  categoria,
  conceptos,
  onEditar,
  onAgregar,
  seleccion,
  onSeleccion,
}: {
  categoria: Categoria;
  conceptos: Concepto[];
  onEditar: (c: Concepto) => void;
  onAgregar: () => void;
  seleccion: Set<number>;
  onSeleccion: (id: number, marcado: boolean) => void;
}) {
  const [abierto, setAbierto] = useState(true);

  const activos = conceptos.filter((c) => c.activo).length;

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="flex items-center bg-muted/40 hover:bg-muted/60 transition-colors">
        <button
          type="button"
          className="flex flex-1 items-center gap-2 px-3 py-2 text-left"
          onClick={() => setAbierto(!abierto)}
        >
          {abierto ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0" />
          )}
          <span className="text-xs font-semibold uppercase tracking-wide flex-1">
            {categoria.nombre}
          </span>
          <Badge variant="secondary" className="text-xs">
            {activos}/{conceptos.length}
          </Badge>
        </button>
        <Button
          variant="ghost"
          size="icon"
          className="mr-1 h-7 w-7"
          title={`Agregar un concepto a ${categoria.nombre}`}
          aria-label={`Agregar un concepto a ${categoria.nombre}`}
          onClick={onAgregar}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {abierto && conceptos.length === 0 && (
        <p className="px-3 py-2 text-xs text-muted-foreground">
          Esta categoría todavía no tiene conceptos.
        </p>
      )}
      {abierto && conceptos.length > 0 && (
        <div className="divide-y">
          {conceptos.map((c) => (
            <FilaConcepto
              key={c.id}
              concepto={c}
              onEditar={() => onEditar(c)}
              seleccionado={seleccion.has(c.id)}
              onSeleccion={(m) => onSeleccion(c.id, m)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ResumenEliminacion({
  revision,
  borrarHistorial,
  onBorrarHistorial,
}: {
  revision: EliminarConceptosResultado;
  borrarHistorial: boolean;
  onBorrarHistorial: (v: boolean) => void;
}) {
  const permitidos = revision.conceptos.filter((c) => !c.bloqueado);
  const sinHistorial = permitidos.filter((c) => c.pagosConDato === 0 && c.obligaciones === 0);
  const conHistorial = permitidos.filter((c) => c.pagosConDato > 0 || c.obligaciones > 0);
  const bloqueados = revision.conceptos.filter((c) => c.bloqueado);
  return (
    <div className="space-y-3 text-sm">
      <p>
        <strong>{sinHistorial.length}</strong> sin historial: se eliminan.
      </p>
      {conHistorial.length > 0 && (
        <div className="space-y-2 rounded-md border p-3">
          <p>
            <strong>{conHistorial.length}</strong> con historial (
            {conHistorial.reduce((a, c) => a + c.pagosConDato, 0)} semana(s) con monto,{' '}
            {conHistorial.reduce((a, c) => a + c.obligaciones, 0)} cuota(s)):
          </p>
          <ul className="max-h-28 list-disc overflow-auto pl-5 text-xs text-muted-foreground">
            {conHistorial.map((c) => (
              <li key={c.id}>{c.nombre}</li>
            ))}
          </ul>
          <div className="flex items-start gap-2">
            <Checkbox
              id="borrar-historial"
              checked={borrarHistorial}
              onCheckedChange={(v) => onBorrarHistorial(v === true)}
            />
            <Label htmlFor="borrar-historial" className="font-normal leading-snug">
              Eliminar también su historial (no se puede deshacer). Si no lo marcas, solo se
              archivan y el historial se conserva.
            </Label>
          </div>
        </div>
      )}
      {bloqueados.length > 0 && (
        <p className="text-amber-700">
          {bloqueados.length} se omiten porque los administra el detalle de su categoría.
        </p>
      )}
    </div>
  );
}

// ─── Pantalla principal ───────────────────────────────────────────────────────

export function ConceptosPantalla() {
  const { data: conceptos, isLoading } = useConceptos({ soloActivos: false });
  const { data: categorias } = useCategorias();
  const { data: proveedores } = useProveedores();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState<Concepto | undefined>();
  const [categoriaNueva, setCategoriaNueva] = useState<number | undefined>();
  // Desde Pagos Semanales, "Agregar concepto" de una categoría vacía llega con ?categoria=ID&nuevo=1.
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const categoria = Number(params.get('categoria'));
    if (categoria && params.get('nuevo') && categorias) {
      setEditando(undefined);
      setCategoriaNueva(categoria);
      setDialogOpen(true);
      setParams({}, { replace: true });
    }
  }, [params, categorias, setParams]);
  const [busqueda, setBusqueda] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState(NINGUN);
  const [tipoFiltro, setTipoFiltro] = useState<'todos' | 'recurrente' | 'esporadico'>('todos');
  const [obligacionesFiltro, setObligacionesFiltro] = useState<'todos' | 'con' | 'sin'>('todos');
  const [mostrarArchivados, setMostrarArchivados] = useState(false);
  const qc = useQueryClient();
  const [seleccion, setSeleccion] = useState<Set<number>>(new Set());
  const [revision, setRevision] = useState<EliminarConceptosResultado | null>(null);
  const [borrarHistorial, setBorrarHistorial] = useState(false);
  const [procesando, setProcesando] = useState(false);

  const marcar = (id: number, marcado: boolean) =>
    setSeleccion((prev) => {
      const nuevo = new Set(prev);
      if (marcado) nuevo.add(id);
      else nuevo.delete(id);
      return nuevo;
    });

  /** Cuenta qué historial tiene lo seleccionado antes de pedir la confirmación. */
  const revisarEliminacion = async () => {
    setProcesando(true);
    const res = await cliente.conceptos.eliminar({ ids: [...seleccion], soloVerificar: true });
    setProcesando(false);
    if (!res.data) {
      toast.error(res.error?.mensaje ?? 'No se pudo revisar la selección');
      return;
    }
    setBorrarHistorial(false);
    setRevision(res.data);
  };

  const confirmarEliminacion = async () => {
    setProcesando(true);
    const res = await cliente.conceptos.eliminar({ ids: [...seleccion], borrarHistorial });
    setProcesando(false);
    if (!res.data) {
      toast.error(res.error?.mensaje ?? 'No se pudo eliminar');
      return;
    }
    const { eliminados, archivados, omitidos } = res.data;
    toast.success(
      [
        eliminados > 0 && `${eliminados} eliminado(s)`,
        archivados > 0 && `${archivados} archivado(s) por tener historial`,
        omitidos > 0 && `${omitidos} omitido(s)`,
      ]
        .filter(Boolean)
        .join(' · ') || 'Sin cambios',
    );
    setRevision(null);
    setSeleccion(new Set());
    qc.invalidateQueries({ queryKey: ['conceptos'] });
    qc.invalidateQueries({ queryKey: ['semana'] });
  };

  const catMap = useMemo(() => {
    const m = new Map<number, Categoria>();
    const recorrer = (cats: Categoria[]) => {
      for (const c of cats) {
        m.set(c.id, c);
        if (c.hijos) recorrer(c.hijos);
      }
    };
    if (categorias) recorrer(categorias);
    return m;
  }, [categorias]);

  const catsRaiz = useMemo(() => categorias ?? [], [categorias]);

  const filtrados = useMemo(() => {
    let items = conceptos ?? [];
    if (!mostrarArchivados) items = items.filter((c) => c.activo);
    if (busqueda)
      items = items.filter((c) => c.nombre.toLowerCase().includes(busqueda.toLowerCase()));
    if (tipoFiltro !== 'todos') items = items.filter((c) => c.tipo === tipoFiltro);
    if (obligacionesFiltro === 'con') items = items.filter((c) => c.tieneObligaciones);
    if (obligacionesFiltro === 'sin') items = items.filter((c) => !c.tieneObligaciones);
    if (categoriaFiltro !== NINGUN) {
      items = items.filter((c) => {
        const cat = catMap.get(c.categoriaId);
        const raizId = cat?.padreId ?? cat?.id;
        return String(raizId) === categoriaFiltro || String(c.categoriaId) === categoriaFiltro;
      });
    }
    return items;
  }, [
    conceptos,
    busqueda,
    categoriaFiltro,
    tipoFiltro,
    obligacionesFiltro,
    mostrarArchivados,
    catMap,
  ]);

  const grupos = useMemo(() => {
    const map = new Map<number, { cat: Categoria; conceptos: Concepto[] }>();
    for (const c of filtrados) {
      const cat = catMap.get(c.categoriaId);
      if (!cat) continue;
      const catRaizId = cat.padreId ?? cat.id;
      const catRaiz = catMap.get(catRaizId) ?? cat;
      if (!map.has(catRaizId)) map.set(catRaizId, { cat: catRaiz, conceptos: [] });
      map.get(catRaizId)!.conceptos.push(c);
    }
    // Sin filtros de texto/tipo, también se muestran las categorías activas que todavía no tienen conceptos
    // (salvo las de detalle, que no llevan conceptos propios): así se les puede agregar el primero con el "+".
    const sinFiltros = !busqueda.trim() && tipoFiltro === 'todos' && obligacionesFiltro === 'todos';
    if (sinFiltros) {
      for (const cat of catMap.values()) {
        if (
          cat.padreId !== null ||
          !cat.activo ||
          cat.conDetalle ||
          cat.fuenteAutomatica ||
          map.has(cat.id)
        )
          continue;
        if (categoriaFiltro !== NINGUN && categoriaFiltro !== String(cat.id)) continue;
        map.set(cat.id, { cat, conceptos: [] });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.cat.orden - b.cat.orden);
  }, [filtrados, catMap, busqueda, tipoFiltro, obligacionesFiltro, categoriaFiltro]);

  const totalActivos = (conceptos ?? []).filter((c) => c.activo).length;
  const totalArchivados = (conceptos ?? []).filter((c) => !c.activo).length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold">Conceptos de Pago</h1>
          <p className="text-sm text-muted-foreground">
            {totalActivos} activos · {totalArchivados} archivados
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {/* Filtro por obligaciones */}
          <Select
            value={obligacionesFiltro}
            onValueChange={(v) => setObligacionesFiltro(v as typeof obligacionesFiltro)}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todas las cuotas</SelectItem>
              <SelectItem value="con">Con obligaciones</SelectItem>
              <SelectItem value="sin">Sin obligaciones</SelectItem>
            </SelectContent>
          </Select>
          {/* Filtro por tipo */}
          <Select value={tipoFiltro} onValueChange={(v) => setTipoFiltro(v as typeof tipoFiltro)}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los tipos</SelectItem>
              <SelectItem value="recurrente">Recurrentes</SelectItem>
              <SelectItem value="esporadico">Esporádicos</SelectItem>
            </SelectContent>
          </Select>
          {/* Filtro por categoría */}
          <Select value={categoriaFiltro} onValueChange={setCategoriaFiltro}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Todas las categorías" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NINGUN}>Todas las categorías</SelectItem>
              {catsRaiz.map((c) => (
                <SelectItem key={c.id} value={String(c.id)}>
                  {c.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Búsqueda */}
          <Input
            placeholder="Buscar concepto..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="w-44"
          />
          {/* Ver archivados */}
          <Button
            variant={mostrarArchivados ? 'secondary' : 'outline'}
            size="sm"
            className="gap-1.5"
            onClick={() => setMostrarArchivados(!mostrarArchivados)}
          >
            <Archive className="h-3.5 w-3.5" />
            Archivados
          </Button>
          {/* Nuevo */}
          <Button
            onClick={() => {
              setEditando(undefined);
              setDialogOpen(true);
            }}
            className="gap-2"
          >
            <Plus className="h-4 w-4" /> Nuevo
          </Button>
        </div>
      </div>

      {seleccion.size > 0 && (
        <div className="flex items-center gap-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm">
          <span className="font-medium">{seleccion.size} seleccionado(s)</span>
          <Button
            variant="destructive"
            size="sm"
            className="gap-1.5"
            onClick={revisarEliminacion}
            disabled={procesando}
          >
            <Trash2 className="h-3.5 w-3.5" /> Eliminar
          </Button>
          <Button variant="outline" size="sm" onClick={() => setSeleccion(new Set())}>
            Quitar selección
          </Button>
        </div>
      )}

      {/* Leyenda de badges */}
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Badge variant="success" className="text-xs">
            Recurrente
          </Badge>
          aparece cada semana
        </span>
        <span className="flex items-center gap-1">
          <Badge variant="warning" className="text-xs">
            Esporádico
          </Badge>
          solo cuando se agrega manualmente
        </span>
        <span className="flex items-center gap-1 ml-2">
          <Archive className="h-3 w-3" /> = archivar (desaparece de Pagos Semanales)
        </span>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground text-sm">Cargando...</div>
      ) : (
        <div className="space-y-2">
          {grupos.map(({ cat, conceptos: items }) => (
            <GrupoConceptos
              key={cat.id}
              categoria={cat}
              conceptos={items}
              onEditar={(c) => {
                setEditando(c);
                setCategoriaNueva(undefined);
                setDialogOpen(true);
              }}
              onAgregar={() => {
                setEditando(undefined);
                setCategoriaNueva(cat.id);
                setDialogOpen(true);
              }}
              seleccion={seleccion}
              onSeleccion={marcar}
            />
          ))}
          {grupos.length === 0 && (
            <div className="text-center py-8 text-muted-foreground text-sm">Sin resultados</div>
          )}
        </div>
      )}

      <Dialog open={revision !== null} onOpenChange={(o) => !o && setRevision(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Eliminar conceptos</DialogTitle>
          </DialogHeader>
          {revision && (
            <ResumenEliminacion
              revision={revision}
              borrarHistorial={borrarHistorial}
              onBorrarHistorial={setBorrarHistorial}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevision(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmarEliminacion}
              disabled={procesando || !revision || revision.conceptos.every((c) => c.bloqueado)}
            >
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {categorias && proveedores && (
        <DialogConcepto
          key={`${editando?.id ?? 'nuevo'}-${categoriaNueva ?? ''}`}
          concepto={editando}
          categoriaInicial={categoriaNueva}
          categorias={categorias}
          proveedores={proveedores}
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setEditando(undefined);
            setCategoriaNueva(undefined);
          }}
        />
      )}
    </div>
  );
}
